import { DecimalPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { LeadStatusService } from 'src/app/shared/services/leadStatus.services';
import { UserServices } from 'src/app/shared/services/userservices';
import { LocalStorageService } from 'src/app/utility/localStorageService';
import { ReportService } from 'src/app/utility/ReportService';

@Component({
  selector: 'app-lead-ageing-report',
  templateUrl: './lead-ageing-report.component.html',
  styleUrls: ['./lead-ageing-report.component.css']
})
export class LeadAgeingReportComponent implements OnInit {

  selectedCounsellors: string[] = [];
  counsellors: any[] = [];

  tableData: any[] = [];
  allData: any[] = [];  // For client-side pagination
  displayedColumns: string[] = [];
  statusColumns: string[] = [];

  totalRecords = 0;
  totalPages = 1;  // For pagination display
  pageSize = 12;
  currentPage = 1;

  isAllSelected = true;
  loading = false;

  fromDate: any;
  toDate: any;

  constructor(
    private userService: UserServices,
    private leadStatusService: LeadStatusService,
    private decimalPipe: DecimalPipe,
    private reportService: ReportService,
    private localStorage: LocalStorageService
  ) { }

  ngOnInit(): void {
    this.getUsersList();
    this.getLeadStatus();

    this.fromDate = this.localStorage.get("showMeLeadsFromDate");
    this.toDate = new Date();
  }

  /** Get dynamic status columns */
  getLeadStatus() {
    this.leadStatusService.getAllLeadStatus()
      .subscribe((resp: any[]) => {
        const dynamicStatusColumns = resp.map(s => s.statusName);
        this.statusColumns = dynamicStatusColumns;

        this.displayedColumns = [
          "Counsellor Name",
          "Student Name",
          "Total Activity",
          "Avg Ageing (Days)",
          ...dynamicStatusColumns
        ];
      });
  }

  /** Load users list */
  getUsersList() {
    this.userService.getUsersActive()
      .subscribe(
        resp => {
          this.counsellors = resp;
          this.selectedCounsellors = this.counsellors.map(c => c.refNo);
          this.isAllSelected = true;
        },
        (error: HttpErrorResponse) => { }
      );
  }

  /** Apply Filter */
  applyFilter() {
  this.loading = true;

  this.reportService.getLeadAgeingReport(
    this.selectedCounsellors,
    this.formatDate(this.fromDate),
    this.formatDate(this.toDate),
    0,  // page (will be ignored since backend doesn't support pagination)
    10000  // size (fetch all)
  ).subscribe({
    next: (res: any[]) => {
      console.log("API response:", res);

      // Backend returns a plain array, not a paginated response
      // So we need to handle pagination locally
      const allData = this.flattenApiResponse(res);

      const statusSet = new Set<string>();
      allData.forEach(row => {
        Object.keys(row).forEach(col => {
          if (!["Counsellor Name", "Student Name", "Total Activity", "Avg Ageing (Days)"].includes(col)) {
            statusSet.add(col);
          }
        });
      });

      this.statusColumns = Array.from(statusSet);
      this.displayedColumns = [
        "Counsellor Name",
        "Student Name",
        "Total Activity",
        "Avg Ageing (Days)",
        ...this.statusColumns
      ];

      // Store all data and apply client-side pagination
      this.allData = allData;
      this.totalRecords = allData.length;
      this.totalPages = Math.ceil(this.totalRecords / this.pageSize);
      this.currentPage = 1;  // Reset to first page
      
      // Apply pagination to show current page data
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      this.tableData = allData.slice(start, end);

      this.loading = false;
    },
    error: () => { this.loading = false; }
  });
}


flattenApiResponse(apiData: any[]): any[] {

  const grouped: any = {};

  apiData.forEach(item => {
    const key = `${item.leadId}-${item.counsellorId}`;

    if (!grouped[key]) {
      grouped[key] = {
        counsellorId: item.counsellorId,
        studentName: item.studentName,
        statusCounts: {},
        ageingDaysArray: []
      };
    }

    grouped[key].statusCounts[item.statusName] =
      (grouped[key].statusCounts[item.statusName] || 0) + 1;

    grouped[key].ageingDaysArray.push(item.ageingDays);
  });

  return Object.values(grouped).map((row: any) => {

    // 🔥 FIXED — Correct mapping from userList
    const counsellor = this.counsellors.find(c => c.refNo == row.counsellorId);
    const counsellorName = counsellor ? counsellor.userName : "Unknown";

    const totalLeads = Object.values(row.statusCounts)
      .reduce((a: number, b: number) => a + b, 0);

    const avgAgeing = row.ageingDaysArray.length
      ? row.ageingDaysArray.reduce((a: number, b: number) => a + b, 0) /
      row.ageingDaysArray.length
      : 0;

    const flatRow: any = {
      "Counsellor Name": counsellorName,
      "Student Name": row.studentName,
      "Total Activity": totalLeads,
      "Avg Ageing (Days)": this.decimalPipe.transform(avgAgeing, '1.0-1')
    };

    Object.keys(row.statusCounts).forEach(status => {
      flatRow[status] = row.statusCounts[status];
    });

    return flatRow;
  });
}


  /** Pagination */
  onPageChange(event: any) {
    this.currentPage = event.pageIndex + 1;
    // Apply client-side pagination
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    this.tableData = this.allData.slice(start, end);
  }

  getPaginationPages(): number[] {
    const pages = [];
    const totalPages = this.totalPages || 1;
    let startPage = Math.max(1, this.currentPage - 2);
    let endPage = Math.min(totalPages, this.currentPage + 2);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  }

  toggleSelectAll() {
    if (this.isAllSelected) {
      this.selectedCounsellors = [];
      this.isAllSelected = false;
    } else {
      this.selectedCounsellors = this.counsellors.map(c => c.refNo);
      this.isAllSelected = true;
    }
  }

  onCounsellorChange() {
    this.isAllSelected = this.selectedCounsellors.length === this.counsellors.length;
  }

  formatDate(date: any): string | null {
    if (!date) return null;
    return new Date(date).toISOString().substring(0, 10);
  }
}
